import EventIcon from '@mui/icons-material/Event';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonIcon from '@mui/icons-material/Person';
import { Card, Link, Menu, MenuItem, Stack } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { HiOutlineOfficeBuilding } from 'react-icons/hi';
import { ClientDTO, NoteDTO, getClientById } from 'wisestep-api';
import {
  getFormatDate,
  WsColors,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
  WsIconProfile,
  WsIconVisa,
} from 'wisestep-base';

import { NotesParent } from '../../../../components/communicationBar/Notes';
import { useUserContext } from '../../../../context';
import getEncodedQueryParams from '../../../../utils/getEncodedQueryParams';
import { downloadCV } from '../../../conversationWidget/utils/cvHelper';
import { ClintPageBreadCrumb } from '../../helper/clientDetailsHelper';

const NoteDescription = styled(WsTypography)(({ expanded }: { expanded: boolean }) => ({
  ...(!expanded && {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: '3',
    WebkitBoxOrient: 'vertical',
  }),
  whiteSpace: 'pre-line',
}));

const NoteContainer = styled(Card)(({ expanded }: { expanded: boolean }) => ({
  ...(!expanded && { cursor: 'pointer' }),
  padding: '1rem',
  marginBottom: '1rem',
}));

interface NoteProps {
  note: NoteDTO;
  onArchive?: () => void;
  onUnarchive?: () => void;
  onEdit?: () => void;
  isClient?: boolean;
  parent: NotesParent;
  parentId?: string;
}

const NoteCaption = ({ children, expanded, hoverText, ...props }) => (
  <WsTypography
    color={WsTypoColors.TERTIARY}
    variant={WsTypoVariants.CAPTION}
    hoverText={hoverText}
    {...(!expanded && { cursor: 'pointer' })}
    {...props}
  >
    {children}
  </WsTypography>
);

const Note = ({ note, onArchive, onUnarchive, onEdit, isClient, parent }: NoteProps) => {
  const { userDTO } = useUserContext();
  const [expanded, setExpanded] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [parentInfo, setParentInfo] = useState<ClientDTO>();
  const menuOpen = Boolean(anchorEl);
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);

  const handleMenuClose = () => setAnchorEl(null);

  const handleEdit = () => {
    handleMenuClose();
    onEdit && onEdit();
  };

  const handleArchive = () => {
    handleMenuClose();
    onArchive && onArchive();
  };

  const handleUnarchive = () => {
    handleMenuClose();
    onUnarchive && onUnarchive();
  };

  useEffect(() => {
    const fetchData = async () => {
      const response = await getClientById(note.client.parentId as string);
      setParentInfo(response.data.data);
    };
    fetchData();
  }, [note.client.parentId]);

  const handleNavigation = (clientId) => {
    return `/client-details?tabIndex=0&isProfile=true&isChild=${
      note.client.parentId != null ? false : true
    }&name=${parentInfo?.name}&clientId=${clientId}&breadCrumbList=${getEncodedQueryParams(
      ClintPageBreadCrumb['CLIENTS']
    )}`;
  };

  return (
    <NoteContainer
      onClick={() => setExpanded(true)}
      elevation={0}
      variant="outlined"
      expanded={expanded}
    >
      <Stack direction="row" justifyContent="space-between">
        <WsTypography
          variant={WsTypoVariants.TITLE}
          color={WsTypoColors.PRIMARY}
          {...(!expanded && { cursor: 'pointer' })}
        >
          {note.heading}
        </WsTypography>
        {parent != NotesParent.USERPROFILE && userDTO?.email === note.createdBy && (
          <IconButton
            aria-label="more"
            id="long-button"
            aria-controls={menuOpen ? 'long-menu' : undefined}
            aria-expanded={menuOpen ? 'true' : undefined}
            aria-haspopup="true"
            onClick={handleMenuClick}
          >
            <MoreVertIcon />
          </IconButton>
        )}
        <Menu
          transitionDuration={0}
          id="long-menu"
          anchorEl={anchorEl}
          open={menuOpen}
          onClose={handleMenuClose}
          elevation={1}
        >
          <MenuItem onClick={handleEdit}>Edit</MenuItem>
          {note.markArchived ? (
            <MenuItem onClick={handleUnarchive}>Unarchive</MenuItem>
          ) : (
            <MenuItem onClick={handleArchive}>Archive</MenuItem>
          )}
        </Menu>
      </Stack>
      <NoteDescription expanded={expanded} {...(!expanded && { cursor: 'pointer' })}>
        {note.description}
      </NoteDescription>
      {note.fileUrl && (
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <WsTypography>{note?.fileName}</WsTypography>
          <FileDownloadOutlinedIcon
            sx={{ color: WsColors.GreyBlack }}
            onClick={() => downloadCV(note?.fileUrl?.split('?')?.[0], note?.fileName)}
          />
        </Stack>
      )}

      <Stack direction="row" spacing={1} sx={{ color: WsColors.GreyBlack, mx: -0.5, mt: 1 }}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <PersonIcon fontSize="inherit" sx={{}} />
          <NoteCaption hoverText="Created By" expanded={expanded}>
            {note.createdByName}
          </NoteCaption>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <EventIcon fontSize="inherit" sx={{}} />
          <NoteCaption hoverText="Created On" expanded={expanded}>
            {getFormatDate(String(note.createdOn))}
          </NoteCaption>
        </Stack>
        {note.noteType && (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <WsIconVisa />
            <NoteCaption hoverText={note.noteType} expanded={expanded}>
              {note.noteType}
            </NoteCaption>
          </Stack>
        )}
        {note.client.name && (
          <Link underline="none" href={handleNavigation(note.client.id)}>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <HiOutlineOfficeBuilding color={WsColors.TertiaryShades700} />
              <NoteCaption hoverText="Client" expanded={expanded}>
                {note.client.name}
              </NoteCaption>
            </Stack>
          </Link>
        )}
        {note.clientContact && isClient == true && (
          <Link
            underline="none"
            href={`/${note.clientContact.id}/client-contact-details?isChild=true&isProfile=true&name=${note.client.name}`}
          >
            <Stack direction="row" alignItems="center" spacing={0.5} onClick={() => {}}>
              <WsIconProfile color={WsColors.TertiaryShades700} />
              <NoteCaption hoverText="Client Contact" expanded={expanded}>
                {`${note.clientContact.firstName} ${note.clientContact.lastName}`}
              </NoteCaption>
            </Stack>
          </Link>
        )}
      </Stack>
    </NoteContainer>
  );
};

export default Note;
